/**
 * Sidebar and BottomNav — the two navigation shells.
 *
 * Sidebar is used by: the Admin app on tablet/desktop, and the POS manager
 * surface. BottomNav is used by: the Staff app and the Admin app on phones.
 *
 * NEITHER COMPONENT KNOWS ABOUT PERMISSIONS. The app passes an already-filtered
 * list of items, because the same registry that filters the menu also builds
 * the router — a menu item and a route must never be able to disagree about who
 * may reach a screen. (And hiding a menu item is not security in any case: the
 * API re-checks every request.)
 *
 * `renderLink` is a render prop so the app supplies its own router `<NavLink>`;
 * this package deliberately does not depend on react-router.
 *
 * Accessibility: `<nav aria-label>`, the active item carries `aria-current`,
 * groups are labelled `<h2>`s inside the nav, and every target is >= 44px.
 */
import type { ReactNode } from 'react';
import { cn } from '../utils.js';

export interface NavItem {
  readonly id: string;
  readonly label: string;
  readonly to: string;
  readonly icon?: ReactNode;
  /** Group heading; items with the same group render together. */
  readonly group?: string;
  /** A count rendered on the right (pending translations, open alerts…). */
  readonly badge?: number | string | null;
}

export interface NavLinkRenderProps {
  readonly item: NavItem;
  readonly className: string;
  readonly children: ReactNode;
}

export interface SidebarProps {
  readonly items: readonly NavItem[];
  /** Order of group headings; unlisted groups follow in insertion order. */
  readonly groupOrder?: readonly string[];
  /** Group heading -> Vietnamese label. */
  readonly groupLabels?: Readonly<Record<string, string>>;
  readonly activePath: string;
  readonly renderLink: (props: NavLinkRenderProps) => ReactNode;
  readonly header?: ReactNode;
  readonly footer?: ReactNode;
  readonly collapsed?: boolean;
  readonly className?: string;
}

function groupItems(
  items: readonly NavItem[],
  order: readonly string[] | undefined,
): readonly [string, readonly NavItem[]][] {
  const map = new Map<string, NavItem[]>();
  for (const item of items) {
    const key = item.group ?? '';
    const bucket = map.get(key);
    if (bucket === undefined) map.set(key, [item]);
    else bucket.push(item);
  }
  if (order === undefined) return Array.from(map.entries());

  const ordered: [string, readonly NavItem[]][] = [];
  for (const key of order) {
    const bucket = map.get(key);
    if (bucket !== undefined) {
      ordered.push([key, bucket]);
      map.delete(key);
    }
  }
  for (const entry of map.entries()) ordered.push(entry);
  return ordered;
}

export function Sidebar({
  items,
  groupOrder,
  groupLabels,
  activePath,
  renderLink,
  header,
  footer,
  collapsed = false,
  className,
}: SidebarProps): React.JSX.Element {
  const groups = groupItems(items, groupOrder);

  return (
    <nav
      aria-label="Điều hướng chính"
      className={cn(
        'flex h-full flex-col border-r border-line bg-surface',
        collapsed ? 'w-16' : 'w-64',
        className,
      )}
    >
      {header !== undefined ? <div className="border-b border-line p-3">{header}</div> : null}

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {groups.map(([group, groupItemsList]) => (
          <div key={group} className="mb-3 last:mb-0">
            {group !== '' && !collapsed ? (
              <h2 className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
                {groupLabels?.[group] ?? group}
              </h2>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              {groupItemsList.map((item) => {
                const active = activePath === item.to || activePath.startsWith(`${item.to}/`);
                return (
                  <li key={item.id}>
                    {renderLink({
                      item,
                      className: cn(
                        'flex min-h-touch items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors',
                        'outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        active
                          ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                          : 'text-fg-muted hover:bg-surface-alt hover:text-fg',
                        collapsed && 'justify-center px-0',
                      ),
                      children: (
                        <>
                          {item.icon !== undefined ? (
                            <span aria-hidden="true" className="shrink-0 text-base">
                              {item.icon}
                            </span>
                          ) : null}
                          {!collapsed ? <span className="flex-1 truncate">{item.label}</span> : null}
                          {!collapsed && item.badge !== undefined && item.badge !== null && item.badge !== 0 ? (
                            <span className="sa-tabular rounded-full bg-danger-solid px-1.5 py-0.5 text-[11px] font-semibold text-white">
                              {item.badge}
                            </span>
                          ) : null}
                        </>
                      ),
                    })}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {footer !== undefined ? <div className="border-t border-line p-3">{footer}</div> : null}
    </nav>
  );
}

export interface BottomNavProps {
  /** Keep to five or fewer; more than that and nothing is reachable by thumb. */
  readonly items: readonly NavItem[];
  readonly activePath: string;
  readonly renderLink: (props: NavLinkRenderProps) => ReactNode;
  readonly className?: string;
}

export function BottomNav({ items, activePath, renderLink, className }: BottomNavProps): React.JSX.Element {
  return (
    <nav
      aria-label="Điều hướng nhanh"
      className={cn(
        'flex items-stretch justify-around border-t border-line bg-surface',
        // Respect the iOS home indicator so the last item is not under it.
        'pb-[env(safe-area-inset-bottom)]',
        className,
      )}
    >
      {items.map((item) => {
        const active = activePath === item.to || activePath.startsWith(`${item.to}/`);
        return (
          <div key={item.id} className="flex-1">
            {renderLink({
              item,
              className: cn(
                'flex min-h-touch-lg flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px] font-medium',
                'outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active ? 'text-brand-600' : 'text-fg-subtle hover:text-fg',
              ),
              children: (
                <>
                  <span aria-hidden="true" className="relative text-lg">
                    {item.icon}
                    {item.badge !== undefined && item.badge !== null && item.badge !== 0 ? (
                      <span className="absolute -right-2 -top-1 min-w-4 rounded-full bg-danger-solid px-1 text-[10px] font-semibold leading-4 text-white">
                        {item.badge}
                      </span>
                    ) : null}
                  </span>
                  <span className="truncate">{item.label}</span>
                </>
              ),
            })}
          </div>
        );
      })}
    </nav>
  );
}
